"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { type DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  createPopoverHandle,
} from "@/components/ui/popover";

export type { DateRange };

export function formatDateRangeLabel(range: DateRange | undefined): string {
  if (!range?.from) {
    return "";
  }
  if (range.to) {
    return `${format(range.from, "LLL dd, y")} - ${format(range.to, "LLL dd, y")}`;
  }
  return format(range.from, "LLL dd, y");
}

export function formatRangeDate(date: Date | undefined): string {
  return date ? format(date, "dd/MM/yyyy") : "";
}

export function formatRangeTime(date: Date | undefined): string {
  return date ? format(date, "HH:mm") : "";
}

type DateRangePickerContextValue = {
  handle: ReturnType<typeof createPopoverHandle>;
  range: DateRange | undefined;
};

const DateRangePickerContext =
  React.createContext<DateRangePickerContextValue | null>(null);

function useDateRangePickerContext() {
  const context = React.useContext(DateRangePickerContext);
  if (!context) {
    throw new Error(
      "DateRangePickerTrigger must be used within a DateRangePicker.",
    );
  }
  return context;
}

export type DateRangePickerProps = {
  /** Controlled selected range */
  value?: DateRange;
  /** Uncontrolled initial range */
  defaultValue?: DateRange;
  /** Called when the range changes */
  onChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  id?: string;
  triggerDisabled?: boolean;
  /** @default 2 */
  numberOfMonths?: number;
  align?: React.ComponentProps<typeof PopoverContent>["align"];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Custom triggers (e.g. inputs). Omit to use the default button trigger. */
  children?: React.ReactNode;
} & Omit<
  React.ComponentProps<typeof Calendar>,
  "mode" | "selected" | "onSelect" | "defaultMonth" | "numberOfMonths"
>;

export function DateRangePicker({
  value,
  defaultValue,
  onChange,
  placeholder = "Pick a date",
  className,
  buttonClassName,
  id,
  triggerDisabled,
  numberOfMonths = 2,
  align = "start",
  open: openProp,
  onOpenChange,
  children,
  ...calendarProps
}: DateRangePickerProps) {
  const handle = React.useMemo(() => createPopoverHandle(), []);
  const [internalRange, setInternalRange] = React.useState<DateRange | undefined>(
    defaultValue,
  );
  const [internalOpen, setInternalOpen] = React.useState(false);

  const isControlled = value !== undefined;
  const range = isControlled ? value : internalRange;
  const isOpenControlled = openProp !== undefined;
  const open = isOpenControlled ? openProp : internalOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isOpenControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isOpenControlled, onOpenChange],
  );

  const handleSelect = (next: DateRange | undefined) => {
    if (!isControlled) {
      setInternalRange(next);
    }
    onChange?.(next);
  };

  const label = formatDateRangeLabel(range);

  if (children) {
    return (
      <DateRangePickerContext.Provider value={{ handle, range }}>
        <div className={className}>{children}</div>
        <Popover handle={handle} open={open} onOpenChange={setOpen}>
          <PopoverContent className="w-auto p-1 shadow-lg" align={align}>
            <Calendar
              mode="range"
              defaultMonth={range?.from}
              selected={range}
              onSelect={handleSelect}
              numberOfMonths={numberOfMonths}
              {...calendarProps}
            />
          </PopoverContent>
        </Popover>
      </DateRangePickerContext.Provider>
    );
  }

  return (
    <DateRangePickerContext.Provider value={{ handle, range }}>
      <div className={className}>
        <Popover handle={handle} open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            disabled={triggerDisabled}
            render={
              <Button
                variant="outline"
                id={id}
                disabled={triggerDisabled}
                data-empty={!range?.from}
                className={cn(
                  "w-full justify-start gap-2 px-2.5 font-normal data-[empty=true]:text-muted-foreground",
                  buttonClassName,
                )}
              />
            }
          >
            <CalendarIcon className="size-4 shrink-0" aria-hidden />
            {label ? <span>{label}</span> : <span>{placeholder}</span>}
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1 shadow-lg" align={align}>
            <Calendar
              mode="range"
              defaultMonth={range?.from}
              selected={range}
              onSelect={handleSelect}
              numberOfMonths={numberOfMonths}
              {...calendarProps}
            />
          </PopoverContent>
        </Popover>
      </div>
    </DateRangePickerContext.Provider>
  );
}

export type DateRangePickerTriggerProps = Omit<
  React.ComponentProps<"input">,
  "type" | "readOnly"
> & {
  triggerId: string;
};

/** Read-only input that opens the parent DateRangePicker popover. */
export function DateRangePickerTrigger({
  triggerId,
  className,
  onClick,
  onFocus,
  ...props
}: DateRangePickerTriggerProps) {
  const { handle } = useDateRangePickerContext();

  return (
    <PopoverTrigger
      handle={handle}
      id={triggerId}
      nativeButton={false}
      render={
        <input
          type="text"
          readOnly
          className={cn("cursor-pointer", className)}
          onClick={onClick}
          onFocus={onFocus}
          {...props}
        />
      }
    />
  );
}
